import { CardStackIcon } from '@radix-ui/react-icons';
import { Skeleton } from '@radix-ui/themes';
import { CommonNavMenu } from 'components/common/layout/nav-menu';
import { SidebarNavigationTrigger } from 'components/main/sidebar/nav-trigger';
import { MachineContext } from 'contexts/machine.context';
import { useActiveListId } from 'hooks/useActiveListId';
import { t } from 'i18next';
import { IMenuAction } from 'interfaces/i-menus';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { ReferenceListType } from 'lib_ts/enums/pitches.enums';
import { IPitchList } from 'lib_ts/interfaces/pitches';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import useListLibraryStore from 'stores/use-list-library-store';

export const MiniMenu = (props: { actions: IMenuAction[] }) => {
  const navigate = useNavigate();

  const lists = useListLibraryStore((s) => s.lists);
  const loading = useListLibraryStore((s) => s.loading);
  const recentListIDs = useListLibraryStore((s) => s.recentListIDs);

  // todo: confirm whether we could just read from the activeList in store instead of using the hook
  const activeListID = useActiveListId();

  const { machine } = useContext(MachineContext);

  const listToAction = useCallback(
    (l: IPitchList, group: string): IMenuAction => {
      return {
        group: group,
        label: l.name,
        onClick: () => {
          if (loading) {
            return;
          }

          if (activeListID === l._id) {
            return;
          }

          navigate(paths.pitches.list(l._id));
        },
      };
    },
    [activeListID, loading, navigate]
  );

  const extendedActions = useMemo(() => {
    const output = [...props.actions];

    const recents = lists
      .filter((l) => recentListIDs.includes(l._id))
      .sort((a, b) =>
        recentListIDs.findIndex((id) => id === a._id) <
        recentListIDs.findIndex((id) => id === b._id)
          ? -1
          : 1
      );

    output.push(...recents.map((l) => listToAction(l, t('main.recent-lists'))));

    /** show all lists sorted alphabetically */
    const groupSortFn = (
      l: IPitchList
    ): {
      sort: number;
      group: string;
    } => {
      switch (l.reference_type) {
        case ReferenceListType.Sample: {
          return {
            sort: 3,
            group: 'constants.pitch-list-samples',
          };
        }

        case ReferenceListType.BallTypeCalibration:
        case ReferenceListType.BurnInTest:
        case ReferenceListType.Repeatability: {
          return {
            sort: 4,
            group: 'constants.pitch-list-references',
          };
        }

        default: {
          break;
        }
      }

      // no type, use parent
      switch (l._parent_def) {
        case PitchListOwner.Team: {
          return {
            sort: 2,
            group: 'constants.pitch-list-team',
          };
        }

        case PitchListOwner.Machine: {
          return {
            sort: 1,
            group: t('constants.pitch-list-x-lists', {
              x: machine.machineID,
            }),
          };
        }

        case PitchListOwner.User:
        default: {
          return {
            sort: 0,
            group: 'constants.pitch-list-personal',
          };
        }
      }
    };

    output.push(
      ...lists
        .sort((a, b) => {
          const aDef = groupSortFn(a);
          const bDef = groupSortFn(b);

          if (aDef.sort !== bDef.sort) {
            return aDef.sort < bDef.sort ? -1 : 1;
          }

          // within the same category, sort by folder so folderless stuff goes to top
          return StringHelper.safeSort(a.folder, b.folder);
        })
        .map((l) => {
          const def = groupSortFn(l);
          const group = t(def.group);
          return listToAction(l, l.folder ? `${group}: ${l.folder}` : group);
        })
    );

    return output;
  }, [props.actions, recentListIDs, listToAction, lists, machine.machineID]);

  if (lists.length === 0) {
    return <Skeleton />;
  }

  return (
    <CommonNavMenu>
      <SidebarNavigationTrigger
        icon={<CardStackIcon />}
        label="main.pitch-lists"
        actions={extendedActions}
      />
    </CommonNavMenu>
  );
};
